import { useEffect, useState } from "react";
import { functionsService, type BillingStatus } from "../services/firebase";

function formatDate(ms: number): string {
  if (!ms) return "-";
  return new Date(ms).toLocaleDateString("ms-MY", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function PublishPayment({
  siteId,
  onStatus,
  mode = "publish",
}: {
  siteId: string;
  onStatus: (allowed: boolean) => void;
  mode?: "publish" | "renew";
}) {
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");
  const [method, setMethod] = useState<"qr" | "other">("qr");
  const [qrFailed, setQrFailed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [clockOffset, setClockOffset] = useState(0);

  const acceptStatus = (status: BillingStatus) => {
    setBilling(status);
    setClockOffset(status.serverNow - Date.now());
    onStatus(status.canPublish);
  };

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const status = await functionsService.getBillingStatus({ siteId });
        if (!cancelled) {
          setBilling(status);
          setClockOffset(status.serverNow - Date.now());
          onStatus(status.canPublish);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          onStatus(false);
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) timer = setTimeout(refresh, 8000);
      }
    };
    void refresh();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(clock);
    };
  }, [siteId, onStatus]);

  const payment = billing?.payment;
  const details = payment?.paymentDetails || billing?.settings;
  const amountMinor = payment?.amountMinor ?? billing?.amountMinor ?? 0;
  const remaining = payment
    ? Math.max(0, Math.ceil((payment.expiresAt - now - clockOffset) / 1000))
    : 0;
  const awaiting = payment?.status === "awaiting_payment" && remaining > 0;
  const pending = payment?.status === "pending";

  const start = async () => {
    if (!billing) return;
    setBusy(true);
    setError("");
    try {
      const result = await functionsService.createPaymentRequest({ siteId });
      acceptStatus({ ...billing, ...result });
      setNow(Date.now());
      setReference("");
      setQrFailed(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!payment || !awaiting) return;
    setBusy(true);
    setError("");
    try {
      await functionsService.submitPaymentRequest({
        paymentRequestId: payment.paymentRequestId,
        paymentReference: reference.trim(),
        paymentMethod: method,
      });
      setBilling((current) =>
        current ? { ...current, payment: { ...payment, status: "pending" } } : current,
      );
      acceptStatus(await functionsService.getBillingStatus({ siteId }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-6 space-y-4" aria-label="Bayaran bulanan laman">
      <h2 className="font-bold text-lg">
        {mode === "renew" ? "Pembaharuan sebulan" : "Bayaran sebelum publish"}
      </h2>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      {!billing ? (
        <p className="text-sm">Menyemak status pembayaran...</p>
      ) : billing.canPublish ? (
        <div className="text-sm space-y-1">
          <p role="status" className="text-green-700">
            {mode === "renew"
              ? "Tempoh laman ini telah dilanjutkan."
              : "Kelayakan publish disahkan."}
          </p>
          <p className="text-surface-600">
            Aktif sehingga <b>{formatDate(billing.paidUntil)}</b>{" "}
            ({billing.daysRemaining} hari lagi).
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-surface-600">
            Laman ini dikenakan bayaran <b>RM{(amountMinor / 100).toFixed(2)}</b> untuk tempoh
            sebulan. Selepas tempoh tamat, laman akan diambil offline sehingga diperbaharui.
          </p>
          {!details ? (
            <p className="text-amber-700">
              Admin belum menyediakan tetapan bayaran. Publish dikunci sehingga bayaran
              disahkan.
            </p>
          ) : (
            <>
              <p className="font-semibold">
                RM{(amountMinor / 100).toFixed(2)}{" "}
                <span className="font-normal">kepada {details.payeeName}</span>
              </p>
              {pending ? (
                <p role="status" className="p-3 bg-amber-50 text-amber-800 rounded-lg">
                  Rujukan bayaran dihantar. Menunggu pengesahan admin; jangan bayar sekali
                  lagi. Status disemak secara automatik.
                </p>
              ) : (
                <>
                  {!awaiting && (
                    <button
                      type="button"
                      className="btn-primary btn-md"
                      disabled={busy}
                      onClick={() => void start()}
                    >
                      {busy ? "Memproses..." : "Mulakan bayaran (2 minit)"}
                    </button>
                  )}
                  {payment?.status === "awaiting_payment" && !remaining && (
                    <p role="status" className="text-amber-700 text-sm">
                      Masa telah tamat. Jika sudah membayar, jangan bayar semula. Mulakan
                      sesi baharu dan hantar rujukan transaksi asal untuk semakan admin.
                    </p>
                  )}
                  {awaiting && (
                    <>
                      <p
                        role="timer"
                        aria-label="Baki masa bayaran"
                        className="font-mono text-2xl"
                      >
                        {Math.floor(remaining / 60)}:
                        {String(remaining % 60).padStart(2, "0")}
                      </p>
                      <p className="text-xs text-surface-500">
                        Hantar rujukan dalam 2 minit. Semakan admin tidak tamat bersama
                        pemasa.
                      </p>
                      <label className="block text-sm">
                        Cara bayaran
                        <select
                          className="input mt-1"
                          value={method}
                          onChange={(e) => setMethod(e.target.value as "qr" | "other")}
                        >
                          <option value="qr">Imbas QR</option>
                          <option value="other">Bayaran lain / pindahan bank</option>
                        </select>
                      </label>
                      {method === "qr" && !qrFailed && (
                        <img
                          src={details.qrImageUrl}
                          alt="QR pembayaran admin"
                          className="w-64 max-w-full mx-auto rounded-lg border"
                          onError={() => {
                            setQrFailed(true);
                            setMethod("other");
                          }}
                        />
                      )}
                      {qrFailed && (
                        <p className="text-sm text-amber-700">
                          QR gagal dimuatkan. Gunakan arahan bayaran alternatif di bawah.
                        </p>
                      )}
                      <p className="text-sm whitespace-pre-wrap">
                        {method === "qr"
                          ? details.paymentInstructions
                          : details.alternatePaymentInstructions ||
                            "Arahan alternatif belum ditetapkan oleh admin. Jangan buat pindahan tanpa maklumat penerima yang sah."}
                      </p>
                      <form onSubmit={submit} className="space-y-3">
                        <label className="block text-sm">
                          Rujukan transaksi
                          <input
                            className="input mt-1"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            maxLength={200}
                            required
                            placeholder="Rujukan daripada resit bank"
                          />
                        </label>
                        <button
                          className="btn-primary btn-md"
                          disabled={
                            busy ||
                            !reference.trim() ||
                            (method === "other" &&
                              !details.alternatePaymentInstructions?.trim())
                          }
                        >
                          {busy ? "Menghantar..." : "Saya sudah bayar - hantar untuk semakan"}
                        </button>
                      </form>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
